import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';
import { useIntl } from 'react-intl';
import { Form, useFormikContext } from 'formik';
import { Modal, modalPropTypes } from '@luigiclaudio/ga-baseline-ui/modal';
import { Col, Row } from '@luigiclaudio/ga-baseline-ui/layout';
import FeedbackContext from '../feedbackContext/FeedbackContext';
import ColorModalCustomHeader from '../colorModal/ColorModalCustomHeader';
import useAllColorUserCache from '../../services/useAllColorUserCache';
import feedbackOnStageUtils from '../../utils/feedbackOnStageUtils';
import ColorEntryAddColor from '../ColorEntries/ColorEntryAddColor';
import colorToolsUtils from '../../utils/colorToolsUtils';
import ColorModalTitle from '../colorModal/ColorModalTitle';
import AddColorDefaultModalContainer from './AddColorDefaultModalContainer';
import ColorModalCloseButton from '../colorModal/ColorModalCloseButton';
import AddColorDefaultModalContent from './AddColorDefaultModalContent';
import ColorToolsContext from '../context/ColorToolsContext';
import CustomColorPicker from '../CustomColorPicker';

const AddColorDefaultModal = ({
    addColor,
    onRequestClose,
    colorValue,
    subtitle,
    footerActions,
    ...props
}) => {
    const intl = useIntl();
    const theme = useContext(ThemeContext);
    const { setFeedbackOnStage } = useContext(FeedbackContext);
    const { setFieldValue } = useFormikContext();
    const { colorOnStage } = useContext(ColorToolsContext);

    const addColorToHex = `#${addColor}`;

    const { allColorsUserFromClient } = useAllColorUserCache(() =>
        setFeedbackOnStage(feedbackOnStageUtils.colorAddedToLibrary(addColorToHex, theme, intl)),
    );

    const isFavorite = allColorsUserFromClient?.allColors?.data?.find(
        (item) => item.color === addColorToHex,
    );

    return (
        <Modal
            title={intl.formatMessage({ id: 'editColor.modal.title' })}
            size="lg"
            customHeader={
                <ColorModalCustomHeader
                    style={{
                        backgroundColor: colorToolsUtils.isValidColor(addColor)
                            ? addColorToHex
                            : colorOnStage,
                    }}
                    isFavorite={!!isFavorite}
                />
            }
            handleOnDismiss={onRequestClose}
            showCloseButton={false}
            footerActions={footerActions}
            {...props}
        >
            <ColorModalCloseButton onRequestClose={onRequestClose} />
            <Form noValidate id="addColorForm">
                <AddColorDefaultModalContent>
                    <Row>
                        <Col xxs={30} xs={28} sm={22} md={15}>
                            <AddColorDefaultModalContainer>
                                <CustomColorPicker
                                    color={
                                        colorToolsUtils.isValidColor(addColor)
                                            ? addColorToHex
                                            : colorOnStage
                                    }
                                    onChange={(color) =>
                                        setFieldValue('addColor', color.hex.slice(1))
                                    }
                                />
                            </AddColorDefaultModalContainer>
                        </Col>
                        <Col xxs={30} md={15}>
                            <AddColorDefaultModalContainer>
                                <ColorModalTitle
                                    colorValue={
                                        colorToolsUtils.isValidColor(addColor)
                                            ? addColorToHex
                                            : colorValue
                                    }
                                    onRequestClose={onRequestClose}
                                    subtitle={subtitle}
                                />
                                <ColorEntryAddColor />
                            </AddColorDefaultModalContainer>
                        </Col>
                    </Row>
                </AddColorDefaultModalContent>
            </Form>
        </Modal>
    );
};

AddColorDefaultModal.propTypes = {
    colorValue: PropTypes.string.isRequired,
    addColor: PropTypes.string,
    onRequestClose: PropTypes.func.isRequired,
    subtitle: PropTypes.string,
    footerActions: modalPropTypes.modalFooterActions,
};

AddColorDefaultModal.defaultProps = {
    addColor: undefined,
    subtitle: undefined,
    footerActions: undefined,
};

export default AddColorDefaultModal;
